import { Link } from "react-router-dom";
import Eyebrow from "@/components/base/Eyebrow";
import MicroLabel from "@/components/base/MicroLabel";

const destinations = [
  {
    label: "Home",
    to: "/",
    note: "The four order types side by side, plus the comparison table.",
  },
  {
    label: "Market orders",
    to: "/market-orders",
    note: "Instant fills — and the price you never quite get.",
  },
  {
    label: "Limit orders",
    to: "/limit-orders",
    note: "Your price or nothing, and the fill that may never arrive.",
  },
  {
    label: "Stop orders",
    to: "/stop-orders",
    note: "A trigger that caps the loss, not the price.",
  },
  {
    label: "Stop-limit orders",
    to: "/stop-limit-orders",
    note: "Trigger and price ceiling in one order, with a gap in between.",
  },
  {
    label: "Order execution",
    to: "/order-execution",
    note: "Spread, slippage, latency and the full cost of a fill.",
  },
  {
    label: "Glossary",
    to: "/glossary",
    note: "Every term on the site, each in a sentence or two.",
  },
];

/**
 * A single flat index of what does exist. On a 404 the useful thing is the
 * whole map, not a single guessed "you probably meant this".
 */
export default function SiteIndex() {
  return (
    <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <Eyebrow>What does exist</Eyebrow>
          <h2 className="mt-4 font-heading font-black uppercase leading-[0.95] tracking-tight text-foreground-950 text-3xl md:text-4xl">
            Every page on the site
          </h2>
          <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600">
            Seven pages, all reachable from here. If one of these was what you were after, the
            original link was probably mistyped rather than broken.
          </p>
          <div className="mt-8 border-t-2 border-foreground-950 pt-5">
            <MicroLabel mark="warn" tone="muted">
              Still stuck
            </MicroLabel>
            <p className="mt-3 text-xs md:text-sm leading-relaxed text-foreground-600">
              The link that sent you here may be on another site, or typed from memory. Neither
              leaves a trace you can inspect — the address bar is the only clue, and it is above.
            </p>
          </div>
        </div>

        <div className="lg:col-span-8 border-t-2 border-foreground-950">
          {destinations.map((item, index) => (
            <Link
              key={item.to}
              to={item.to}
              className="group flex items-start gap-4 -mx-4 px-4 py-5 border-b-2 border-foreground-950 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <span
                className="shrink-0 w-8 font-heading font-black text-2xl leading-none text-stroke text-foreground-950 mt-0.5 transition-colors group-hover:text-accent-600"
                aria-hidden="true"
              >
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-heading font-bold uppercase tracking-tight text-base md:text-lg leading-snug text-foreground-950">
                  {item.label}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.note}</p>
              </div>
              <i className="ri-arrow-right-line text-foreground-950 ml-auto mt-1 transition-transform group-hover:translate-x-1 group-hover:text-accent-600"></i>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}